import { Center, Spinner, Text } from "@chakra-ui/react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import "./style.css";
import { useInduction } from "../hooks/useInduction";
import { inductionService } from "../services/inductionService";
import { COLORS } from "../Token";
import { InductionFeedback } from "../Types";
import { WelcomeScreen } from "../components/WelcomeScreen";
import { InductionPlayer } from "../components/InductionPlayer";
import { CompletionScreen } from "../components/CompletionScreen";

type Stage = "welcome" | "player" | "done";

export default function InductionPage() {
  const navigate = useNavigate();
  const induction = useInduction();
  const [stage, setStage] = useState<Stage>("welcome");

  useEffect(() => {
    let cancelled = false;
    inductionService
      .getStatus()
      .then((status) => {
        if (!cancelled) {
          // If feedback is already completed but dual approvals are pending, show the completion waiting screen directly
          if (status.isCompleted && !status.bothApproved) {
            setStage("done");
          }
        }
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  if (induction.loading) {
    return (
      <Center minH="100vh" bg={COLORS.forest}>
        <Spinner size="xl" color={COLORS.mint} thickness="4px" />
      </Center>
    );
  }

  if (!induction.data || !induction.section) {
    return (
      <Center minH="100vh" bg={COLORS.page} px={6}>
        <Text fontSize="lg" color={COLORS.ink}>
          {induction.error ?? "Your induction is not available right now."}
        </Text>
      </Center>
    );
  }

  const handleFinish = async (feedback: InductionFeedback) => {
    const ok = await induction.finish(feedback);
    if (ok) setStage("done");
  };

  if (stage === "welcome") {
    return (
      <WelcomeScreen
        employeeName={induction.data.employeeName}
        companyName={induction.data.companyName}
        branchName={induction.data.branchName}
        departmentName={induction.data.departmentName}
        designationName={induction.data.designationName}
        joiningDate={induction.data.joiningDate}
        reportingManager={induction.data.reportingManager}
        buddyName={induction.data.buddyName}
        employeeCode={induction.data.employeeCode}
        sections={induction.data.sections}
        resuming={induction.index > 0}
        onStart={() => setStage("player")}
      />
    );
  }

  if (stage === "done") {
    return (
      <CompletionScreen
        employeeName={induction.data.employeeName}
        companyName={induction.data.companyName}
        branchName={induction.data.branchName}
        businessName={induction.data.businessName}
      />
    );
  }

  return (
    <InductionPlayer
      data={induction.data}
      section={induction.section}
      index={induction.index}
      lastIndex={induction.lastIndex}
      percent={induction.percent}
      submitting={induction.submitting}
      error={induction.error}
      onSelect={induction.goTo}
      onNext={induction.next}
      onPrev={induction.prev}
      onFinish={handleFinish}
    />
  );
}
